import { supabase } from '../supabase';
import { UserGame } from '../../types';
import { UserInteractionDataService } from './userInteractionDataService';

/**
 * Serviço para gerenciar jogos do usuário, incluindo coleção e jogos avaliados
 */
export class UserGameService {
  private userInteractionDataService: UserInteractionDataService;

  constructor(userInteractionDataService: UserInteractionDataService) {
    this.userInteractionDataService = userInteractionDataService;
    console.log('[UserGameService constructor] UserInteractionDataService initialized:', 
                 this.userInteractionDataService ? 'Instance OK' : 'Instance FAILED');
  }

  /**
   * Obtém a coleção completa de jogos do usuário
   */
  public async getUserCollection(userId: string): Promise<any[]> {
    try {
      const { data, error } = await supabase
        .from('user_games')
        .select('*, games!user_games_game_id_fkey(*)')
        .eq('user_id', userId)
        .eq('owned', true);
        
      if (error) {
        console.error('Erro ao obter coleção do usuário:', error);
        return [];
      }
      
      console.log(`Obtidos ${data?.length || 0} jogos da coleção do usuário ${userId}`);
      
      // Mapear 'games' para 'game' para manter compatibilidade
      return (data || []).map(item => ({
        ...item,
        game: item.games
      }));
    } catch (error) {
      console.error('Erro ao obter coleção do usuário:', error);
      return [];
    }
  }
  
  /**
   * Obtém apenas os IDs dos jogos na coleção do usuário
   */
  public async getCollectionGameIds(userId: string): Promise<string[]> {
    try {
      const { data, error } = await supabase
        .from('user_games')
        .select('game_id')
        .eq('user_id', userId)
        .eq('owned', true);
        
      if (error) {
        console.error('Erro ao obter IDs da coleção:', error);
        return [];
      }
      
      return (data || []).map(item => item.game_id);
    } catch (error) {
      console.error('Erro ao obter IDs da coleção:', error);
      return [];
    }
  }
  
  /**
   * Obtém IDs de jogos que o usuário marcou como "não gostei"
   */
  public async getDislikedGameIds(userId: string, providedIds: string[] = []): Promise<string[]> {
    try {
      // Se já temos IDs fornecidos, usar como base
      if (providedIds && providedIds.length > 0) {
        return providedIds;
      }
      
      // Buscar jogos com baixa avaliação
      const { data: lowRatedGames, error: ratingError } = await supabase
        .from('user_games')
        .select('game_id')
        .eq('user_id', userId)
        .lt('rating', 2); // Jogos com avaliação menor que 2 são considerados "não gostei"
        
      if (ratingError) {
        console.error('Erro ao obter jogos com baixa avaliação:', ratingError);
        return [];
      }
      
      // Buscar interações negativas (dislikes explícitos)
      const { data: dislikedInteractions, error: interactionError } = await supabase
        .from('user_interactions')
        .select('game_id')
        .eq('user_id', userId)
        .eq('interaction_type', 'dislike');
        
      if (interactionError) {
        console.error('Erro ao obter interações negativas:', interactionError);
        // Se houver erro, retornar pelo menos os jogos com baixa avaliação
        return (lowRatedGames || []).map(item => item.game_id);
      }
      
      // Buscar recomendações com feedback negativo (dislike)
      const { data: dislikedRecommendations, error: recoError } = await supabase
        .from('recommendations')
        .select('game_id')
        .eq('user_id', userId)
        .eq('liked', false);
        
      if (recoError) {
        console.error('Erro ao obter recomendações com dislike:', recoError);
      }
      
      // Combinar os conjuntos de IDs
      const lowRatedIds = (lowRatedGames || []).map(item => item.game_id);
      const dislikedIds = (dislikedInteractions || []).map(item => item.game_id);
      const dislikedRecoIds = (dislikedRecommendations || []).map(item => item.game_id);
      
      // Usar Set para eliminar duplicatas
      const combinedIds = [...new Set([...lowRatedIds, ...dislikedIds, ...dislikedRecoIds])];
      
      console.log(`Encontrados ${combinedIds.length} jogos para excluir (${lowRatedIds.length} com baixa avaliação, ${dislikedIds.length} com dislike explícito, ${dislikedRecoIds.length} recomendações rejeitadas)`);
      
      return combinedIds;
    } catch (error) {
      console.error('Erro ao obter jogos não curtidos:', error);
      return [];
    }
  }
  
  /**
   * Obtém IDs de jogos que o usuário marcou como "gostei" (upvote)
   */
  public async getLikedGameIds(userId: string): Promise<string[]> {
    try {
      // Buscar interações positivas (upvotes explícitos)
      const { data: upvotedInteractions, error: upvoteError } = await supabase
        .from('user_interactions')
        .select('game_id')
        .eq('user_id', userId)
        .eq('interaction_type', 'upvote');
        
      if (upvoteError) {
        console.error('Erro ao obter interações positivas:', upvoteError);
        // Mesmo com erro, tentaremos buscar outras fontes de "like"
      }
      
      // Buscar recomendações com feedback positivo (upvote)
      const { data: likedRecommendations, error: recoError } = await supabase
        .from('recommendations')
        .select('game_id')
        .eq('user_id', userId)
        .eq('liked', true);
        
      if (recoError) {
        console.error('Erro ao obter recomendações com upvote:', recoError);
      }

      // === INÍCIO DA ADIÇÃO ===
      // Buscar jogos com avaliação positiva (>= 3)
      const { data: highRatedGames, error: ratingError } = await supabase
        .from('user_games')
        .select('game_id')
        .eq('user_id', userId)
        .gte('rating', 3); // >= 3 é considerado "like" implícito

      if (ratingError) {
        console.error('Erro ao obter jogos com alta avaliação:', ratingError);
        // Continuar mesmo em caso de erro aqui, usando os outros dados
      }
      // === FIM DA ADIÇÃO ===
      
      const upvotedIds = (upvotedInteractions || []).map(item => item.game_id);
      const likedRecoIds = (likedRecommendations || []).map(item => item.game_id);
      // === INÍCIO DA ADIÇÃO ===
      const highRatedIds = (highRatedGames || []).map(item => item.game_id);
      // === FIM DA ADIÇÃO ===
      
      // Combinar e remover duplicatas
      const combinedIds = [...new Set([
          ...upvotedIds,
          ...likedRecoIds,
          ...highRatedIds // Incluir IDs bem avaliados
        ])];
      
      console.log(`Encontrados ${combinedIds.length} jogos com upvote/like (${upvotedIds.length} interações, ${likedRecoIds.length} recomendações aceitas, ${highRatedIds.length} bem avaliados)`);
      
      return combinedIds;
    } catch (error) {
      console.error('Erro ao obter jogos curtidos:', error);
      return [];
    }
  }
  
  /**
   * Obtém IDs de todos os jogos que já receberam algum tipo de feedback
   * Isso inclui jogos que foram avaliados, tiveram interações ou recomendações com feedback
   */
  public async getAllInteractedGameIds(userId: string): Promise<string[]> {
    try {
      // 1. Obter todos os jogos avaliados (qualquer rating) - estes SEMPRE devem ser excluídos
      const { data: ratedGames, error: ratingError } = await supabase
        .from('user_games')
        .select('game_id')
        .eq('user_id', userId)
        .not('rating', 'is', null);
        
      if (ratingError) {
        console.error('[ERRO] getAllInteractedGameIds - Erro ao obter jogos avaliados:', ratingError);
        return []; // Em caso de erro, retorna lista vazia para evitar recomendações inválidas
      }
      
      const ratedIds = (ratedGames || []).map(item => item.game_id);
      console.log(`[DEBUG] getAllInteractedGameIds - Encontrados ${ratedIds.length} jogos com avaliação`);
      
      // 2. Obter todas as interações (upvotes e downvotes)
      const { data: interactions, error: interactionError } = await supabase
        .from('user_interactions')
        .select('game_id')
        .eq('user_id', userId);
        
      if (interactionError) {
        console.error('[ERRO] getAllInteractedGameIds - Erro ao obter interações com jogos:', interactionError);
        // Se falhar aqui, pelo menos retornar os jogos avaliados para não recomendar jogos já avaliados
        return ratedIds;
      }
      
      const interactionIds = (interactions || []).map(item => item.game_id);
      console.log(`[DEBUG] getAllInteractedGameIds - Encontrados ${interactionIds.length} jogos com interações explícitas`);
      
      // 3. Obter recomendações COM FEEDBACK (like/dislike) - Usando .not('liked', 'is', null)
      const { data: feedbackRecommendationsData, error: recoError } = await supabase
        .from('recommendations')
        .select('game_id') 
        .eq('user_id', userId)
        .not('liked', 'is', null);
        
      if (recoError) {
        console.error('[ERRO] getAllInteractedGameIds - Erro ao obter recomendações com feedback:', recoError);
        // Mesmo com erro, continuar com os dados que temos até agora
        return [...new Set([...ratedIds, ...interactionIds])];
      }
      
      // Extrair game_ids das recomendações com feedback
      const feedbackRecommendationIds = (feedbackRecommendationsData || []).map(item => item.game_id);
            
      console.log(`[DEBUG] getAllInteractedGameIds - Encontrados ${feedbackRecommendationIds.length} jogos de recomendações com feedback (liked não é nulo)`);
      
      // 4. Verificar também na tabela user_games para jogos na coleção do usuário
      const { data: userOwnedGames, error: ownedError } = await supabase
        .from('user_games')
        .select('game_id')
        .eq('user_id', userId)
        .eq('owned', true);
      
      if (ownedError) {
        console.error('[ERRO] getAllInteractedGameIds - Erro ao obter jogos na coleção:', ownedError);
      }
      
      const ownedIds = (userOwnedGames || []).map(item => item.game_id);
      console.log(`[DEBUG] getAllInteractedGameIds - Encontrados ${ownedIds.length} jogos na coleção do usuário`);
      
      // 5. Verificar também jogos na wishlist do usuário
      const { data: userWishlistGames, error: wishlistError } = await supabase
        .from('user_games')
        .select('game_id')
        .eq('user_id', userId)
        .eq('wishlist', true);
      
      if (wishlistError) {
        console.error('[ERRO] getAllInteractedGameIds - Erro ao obter jogos na wishlist:', wishlistError);
      }
      
      const wishlistIds = (userWishlistGames || []).map(item => item.game_id);
      console.log(`[DEBUG] getAllInteractedGameIds - Encontrados ${wishlistIds.length} jogos na wishlist do usuário`);
      
      // 6. Eliminação de duplicatas com Set - combinando TODOS os conjuntos
      const combinedIds = [...new Set([
        ...ratedIds, 
        ...interactionIds, 
        ...feedbackRecommendationIds, // Usar a nova lista
        ...ownedIds,
        ...wishlistIds
      ])];
      
      console.log(`[DEBUG] getAllInteractedGameIds - Total de ${combinedIds.length} jogos com interações (${ratedIds.length} avaliados, ${interactionIds.length} com interações diretas, ${feedbackRecommendationIds.length} recomendações com feedback, ${ownedIds.length} na coleção, ${wishlistIds.length} na wishlist)`);
      
      // Log Detalhado da Lista Final
      console.log('[DETAILED DEBUG] getAllInteractedGameIds - Lista final de IDs retornados:', combinedIds);
      
      // 7. Verificação de consistência - todos os jogos avaliados devem estar na lista final
      const missedRatedGames = ratedIds.filter(id => !combinedIds.includes(id));
      if (missedRatedGames.length > 0) {
        console.error(`[ERRO] getAllInteractedGameIds - Inconsistência: ${missedRatedGames.length} jogos avaliados não estão na lista final!`);
        // Adicionar os jogos faltantes manualmente para garantir
        combinedIds.push(...missedRatedGames);
      }
      
      return combinedIds;
    } catch (error) {
      console.error('[ERRO] getAllInteractedGameIds - Erro ao obter jogos com interações:', error);
      return [];
    }
  }
  
  /**
   * Constrói lista de jogos a serem excluídos das recomendações
   */
  public buildExcludeGamesList(collectionIds: string[], dislikedIds: string[], likedIds: string[] = []): string[] {
    // Combinar os IDs de jogos na coleção, não curtidos e com upvote
    const excludeSet = new Set([...collectionIds, ...dislikedIds, ...likedIds]);
    const result = Array.from(excludeSet);
    
    console.log(`Lista de exclusão construída: ${result.length} jogos (${collectionIds.length} na coleção, ${dislikedIds.length} disliked, ${likedIds.length} liked)`);
    
    return result;
  }
  
  /**
   * Filtra jogos não desejados da lista de recomendações
   */
  public filterOutDislikedAndOwnedGames(games: any[], dislikedIds: string[], collectionIds: string[]): any[] {
    // Usar o método buildExcludeGamesList para criar o conjunto de exclusão
    const excludeList = this.buildExcludeGamesList(collectionIds, dislikedIds);
    
    // Criar um conjunto para pesquisa rápida
    const excludeSet = new Set(excludeList);
    
    console.log(`Filtrando ${games.length} jogos usando lista de exclusão com ${excludeSet.size} jogos`);
    
    // Filtrar jogos que não estão no conjunto de exclusão
    // Suporta tanto game.id (formato de AnyGame) quanto game.game_id (formato de UserGame)
    const filteredGames = games.filter(game => {
      const gameId = game.id || game.game_id;
      if (!gameId) {
        console.warn('Encontrado jogo sem ID durante filtragem:', game);
        return true; // Manter jogos sem ID para evitar perda de recomendações
      }
      return !excludeSet.has(gameId);
    });
    
    console.log(`Após filtragem: ${filteredGames.length} jogos mantidos, ${games.length - filteredGames.length} jogos removidos`);
    
    return filteredGames;
  }

  /**
   * Adiciona um jogo à coleção do usuário ou atualiza se já existe
   */
  public async addUserGame(userId: string, gameId: string, details: Partial<UserGame>): Promise<UserGame | null> {
    try {
      const { data, error } = await supabase
        .from('user_games')
        .upsert({ user_id: userId, game_id: gameId, ...details }, { onConflict: 'user_id, game_id' })
        .select('*, games!user_games_game_id_fkey(*)')
        .single();

      if (error) {
        console.error('Erro ao adicionar/atualizar jogo do usuário:', error);
        return null;
      }
      
      if (data && userId) { // Adicionado userId para a chamada de cache
        await this.userInteractionDataService.clearRecommendationCacheForUser(userId);
      }
      return data ? { ...data, game: data.games } : null;
    } catch (error) {
      console.error('Exceção ao adicionar/atualizar jogo do usuário:', error);
      return null;
    }
  }

  /**
   * Atualiza os detalhes de um jogo específico na coleção de um usuário
   */
  public async updateUserGame(userId: string, gameId: string, updates: Partial<UserGame>): Promise<UserGame | null> {
    try {
      const { data, error } = await supabase
        .from('user_games')
        .update(updates)
        .eq('user_id', userId)
        .eq('game_id', gameId)
        .select('*, games!user_games_game_id_fkey(*)')
        .single();

      if (error) {
        console.error('Erro ao atualizar jogo do usuário:', error);
        return null;
      }
      
      if (data && userId) { // Adicionado userId para a chamada de cache
         await this.userInteractionDataService.clearRecommendationCacheForUser(userId);
      }
      return data ? { ...data, game: data.games } : null;
    } catch (error) {
      console.error('Exceção ao atualizar jogo do usuário:', error);
      return null;
    }
  }

  /**
   * Remove um jogo da coleção do usuário.
   */
  public async removeUserGame(userId: string, gameId: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('user_games')
        .delete()
        .eq('user_id', userId)
        .eq('game_id', gameId);

      if (error) {
        console.error('Erro ao remover jogo do usuário:', error);
        return false;
      }
      
      // Limpar cache após remoção bem-sucedida
      if (userId) {
        await this.userInteractionDataService.clearRecommendationCacheForUser(userId);
      }
      return true;
    } catch (error) {
      console.error('Exceção ao remover jogo do usuário:', error);
      return false;
    }
  }
  
  /**
   * Define ou atualiza a avaliação de um jogo pelo usuário.
   */
  public async rateGame(userId: string, gameId: string, rating: number | null, complexityRating?: number | null): Promise<UserGame | null> {
    try {
      const updateData: Partial<UserGame> & { user_id: string; game_id: string } = {
        user_id: userId,
        game_id: gameId,
        rating: rating,
      };
      if (complexityRating !== undefined) {
        updateData.complexity_rating = complexityRating;
      }

      const { data, error } = await supabase
        .from('user_games')
        .upsert(updateData, { onConflict: 'user_id, game_id' })
        .select('*, games!user_games_game_id_fkey(*)')
        .single();

      if (error) {
        console.error('Erro ao avaliar jogo:', error);
        return null;
      }
      
      if (data && userId) {
        await this.userInteractionDataService.clearRecommendationCacheForUser(userId);
      }
      return data ? { ...data, game: data.games } : null;
    } catch (error) {
      console.error('Exceção ao avaliar jogo:', error);
      return null;
    }
  }

  /**
   * Altera o estado de "owned" (pertence / não pertence) de um jogo para o usuário.
   */
  public async toggleGameOwnership(userId: string, gameId: string, owned: boolean): Promise<UserGame | null> {
    try {
      const { data, error } = await supabase
        .from('user_games')
        .upsert({ user_id: userId, game_id: gameId, owned: owned }, { onConflict: 'user_id, game_id' })
        .select('*, games!user_games_game_id_fkey(*)')
        .single();

      if (error) {
        console.error('Erro ao alterar ownership do jogo:', error);
        return null;
      }
      
      if (data && userId) {
        await this.userInteractionDataService.clearRecommendationCacheForUser(userId);
      }
      return data ? { ...data, game: data.games } : null;
    } catch (error) {
      console.error('Exceção ao alterar ownership do jogo:', error);
      return null;
    }
  }

  /**
   * Adiciona ou remove um jogo da lista de desejos do usuário.
   */
  public async toggleGameWishlist(userId: string, gameId: string, onWishlist: boolean): Promise<UserGame | null> {
    try {
      const { data, error } = await supabase
        .from('user_games')
        .upsert({ user_id: userId, game_id: gameId, wishlist: onWishlist }, { onConflict: 'user_id, game_id' })
        .select('*, games!user_games_game_id_fkey(*)')
        .single();

      if (error) {
        console.error('Erro ao alterar wishlist do jogo:', error);
        return null;
      }
      
      if (data && userId) {
        await this.userInteractionDataService.clearRecommendationCacheForUser(userId);
      }
      return data ? { ...data, game: data.games } : null;
    } catch (error) {
      console.error('Exceção ao alterar wishlist do jogo:', error);
      return null;
    }
  }
} 